import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';

export default function ProjectIntegrations({ board, api, onDataChange, isOpen, onClose }) {
  const [integrations, setIntegrations] = useState([]);
  const [availableIntegrations, setAvailableIntegrations] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [config, setConfig] = useState({});

  // Available integrations list
  const integrationTypes = [
    {
      id: 'slack',
      name: 'Slack',
      description: 'Notificações automáticas no Slack para atualizações de projeto',
      icon: '💬',
      category: 'Comunicação',
      fields: [
        { key: 'webhook_url', label: 'Webhook URL', type: 'text', required: true },
        { key: 'channel', label: 'Canal', type: 'text', required: true },
        { key: 'notify_on_create', label: 'Notificar ao criar item', type: 'boolean' },
        { key: 'notify_on_complete', label: 'Notificar ao concluir item', type: 'boolean' }
      ]
    },
    {
      id: 'github',
      name: 'GitHub',
      description: 'Sincronizar issues do GitHub com itens do projeto',
      icon: '🐙',
      category: 'Desenvolvimento',
      fields: [
        { key: 'repo_url', label: 'Repository URL', type: 'text', required: true },
        { key: 'access_token', label: 'GitHub Token', type: 'password', required: true },
        { key: 'sync_issues', label: 'Sincronizar Issues', type: 'boolean' },
        { key: 'create_branches', label: 'Criar branches automaticamente', type: 'boolean' }
      ]
    },
    {
      id: 'google_calendar',
      name: 'Google Calendar',
      description: 'Sincronizar prazos com Google Calendar',
      icon: '📅',
      category: 'Produtividade',
      fields: [
        { key: 'calendar_id', label: 'Calendar ID', type: 'text', required: true },
        { key: 'api_key', label: 'Google API Key', type: 'password', required: true },
        { key: 'sync_deadlines', label: 'Sincronizar prazos', type: 'boolean' }
      ]
    },
    {
      id: 'zapier',
      name: 'Zapier',
      description: 'Conectar com centenas de aplicativos via Zapier',
      icon: '⚡',
      category: 'Automação',
      fields: [
        { key: 'webhook_url', label: 'Zapier Webhook', type: 'text', required: true },
        { key: 'trigger_events', label: 'Eventos para disparar', type: 'multiselect', 
          options: ['item_created', 'item_updated', 'item_completed', 'status_changed'] }
      ]
    },
    {
      id: 'email',
      name: 'Email Notifications',
      description: 'Notificações por email para atualizações importantes',
      icon: '📧',
      category: 'Comunicação',
      fields: [
        { key: 'smtp_server', label: 'SMTP Server', type: 'text', required: true },
        { key: 'smtp_port', label: 'SMTP Port', type: 'number', required: true },
        { key: 'username', label: 'Username', type: 'text', required: true },
        { key: 'password', label: 'Password', type: 'password', required: true },
        { key: 'from_email', label: 'From Email', type: 'email', required: true }
      ]
    },
    {
      id: 'time_tracking',
      name: 'Time Tracking',
      description: 'Integração com ferramentas de controle de tempo',
      icon: '⏱️',
      category: 'Produtividade',
      fields: [
        { key: 'service', label: 'Serviço', type: 'select', 
          options: ['Toggl', 'Clockify', 'RescueTime', 'Harvest'], required: true },
        { key: 'api_token', label: 'API Token', type: 'password', required: true },
        { key: 'auto_start', label: 'Iniciar automaticamente', type: 'boolean' }
      ]
    }
  ];

  useEffect(() => {
    if (isOpen && board) {
      fetchIntegrations();
    }
  }, [isOpen, board]);

  const fetchIntegrations = async () => {
    try {
      const response = await api.get(`/projects/boards/${board.id}/integrations`);
      setIntegrations(response.data || []);
    } catch (error) {
      console.error('Error fetching integrations:', error);
      setIntegrations([]);
    }
  };

  const handleAddIntegration = async () => {
    if (!selectedIntegration) return;

    setLoading(true);
    try {
      const integration = integrationTypes.find(i => i.id === selectedIntegration);
      const integrationData = {
        board_id: board.id,
        type: selectedIntegration,
        name: integration.name,
        config: config,
        enabled: true
      };

      await api.post(`/projects/boards/${board.id}/integrations`, integrationData);
      
      toast.success(`Integração ${integration.name} adicionada com sucesso!`);
      fetchIntegrations();
      setShowAddModal(false);
      setSelectedIntegration(null);
      setConfig({});
      
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      console.error('Error adding integration:', error);
      toast.error('Erro ao adicionar integração');
    } finally {
      setLoading(false);
    }
  };

  const toggleIntegration = async (integrationId, enabled) => {
    try {
      await api.put(`/projects/integrations/${integrationId}/toggle`, { enabled });
      
      setIntegrations(prev =>
        prev.map(integration =>
          integration.id === integrationId ? { ...integration, enabled } : integration
        )
      );
      
      toast.success(enabled ? 'Integração ativada' : 'Integração desativada');
    } catch (error) {
      console.error('Error toggling integration:', error);
      toast.error('Erro ao alterar status da integração');
    }
  };

  const removeIntegration = async (integrationId) => {
    if (!confirm('Tem certeza que deseja remover esta integração?')) return;

    try {
      await api.delete(`/projects/integrations/${integrationId}`);
      
      setIntegrations(prev => prev.filter(integration => integration.id !== integrationId));
      toast.success('Integração removida com sucesso');
      
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      console.error('Error removing integration:', error);
      toast.error('Erro ao remover integração');
    }
  };

  const testIntegration = async (integrationId) => {
    try {
      setLoading(true);
      await api.post(`/projects/integrations/${integrationId}/test`);
      toast.success('Teste de integração realizado com sucesso!');
    } catch (error) {
      console.error('Error testing integration:', error);
      toast.error('Erro ao testar integração');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Comunicação': 'bg-blue-500',
      'Desenvolvimento': 'bg-green-500',
      'Produtividade': 'bg-purple-500',
      'Automação': 'bg-orange-500'
    };
    return colors[category] || 'bg-gray-500';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Comunicação': '💬',
      'Desenvolvimento': '💻',
      'Produtividade': '📊',
      'Automação': '⚡'
    };
    return icons[category] || '🔧';
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex h-[92vh] max-h-[92vh] w-[calc(100vw-1rem)] max-w-6xl flex-col overflow-hidden p-0 sm:w-[calc(100vw-2rem)]">
        <DialogHeader className="shrink-0 border-b border-slate-200 px-4 py-4 sm:px-6 sm:py-5">
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <span className="text-2xl">🔗</span>
            <span>Integrações do Projeto</span>
            <Badge variant="outline" className="ml-2">
              {integrations.filter(i => i.enabled).length} ativa(s)
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex flex-1 flex-col gap-6 overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
          
          {/* Header with Add Button */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <p className="max-w-3xl text-gray-600">
              Conecte seu projeto com ferramentas externas para automatizar workflows
            </p>
            <Button onClick={() => setShowAddModal(true)} className="w-full sm:w-auto">
              <span className="mr-2">➕</span>
              Nova Integração
            </Button>
          </div>

          {/* Active Integrations */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Integrações Ativas</h3>
            
            {integrations.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <div className="text-4xl mb-4">🔗</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Nenhuma integração configurada
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Adicione integrações para conectar seu projeto com ferramentas externas
                  </p>
                  <Button onClick={() => setShowAddModal(true)}>
                    Adicionar Primeira Integração
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {integrations.map((integration) => {
                  const integrationDef = integrationTypes.find(t => t.id === integration.type);
                  
                  return (
                    <Card key={integration.id} className={`${integration.enabled ? 'border-green-200' : 'border-gray-200'}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="text-2xl">{integrationDef?.icon}</span>
                            <div>
                              <h4 className="font-medium">{integration.name}</h4>
                              <Badge 
                                variant={integration.enabled ? 'default' : 'secondary'}
                                className="text-xs mt-1"
                              >
                                {integration.enabled ? 'Ativa' : 'Inativa'}
                              </Badge>
                            </div>
                          </div>
                          
                          <Switch
                            checked={integration.enabled}
                            onCheckedChange={(enabled) => toggleIntegration(integration.id, enabled)}
                          />
                        </div>
                      </CardHeader>
                      
                      <CardContent>
                        <p className="text-sm text-gray-600 mb-3">
                          {integrationDef?.description}
                        </p>
                        
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => testIntegration(integration.id)}
                            disabled={!integration.enabled || loading}
                          >
                            🧪 Testar
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => removeIntegration(integration.id)}
                          >
                            🗑️ Remover
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Available Integrations */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Integrações Disponíveis</h3>
            
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {integrationTypes
                .filter(type => !integrations.some(i => i.type === type.id))
                .map((integration) => (
                <Card 
                  key={integration.id} 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => {
                    setSelectedIntegration(integration.id);
                    setShowAddModal(true);
                  }}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start space-x-3">
                      <div className="text-3xl">{integration.icon}</div>
                      <div className="flex-1">
                        <h4 className="font-medium">{integration.name}</h4>
                        <Badge 
                          className={`text-xs text-white mt-1 ${getCategoryColor(integration.category)}`}
                        >
                          {getCategoryIcon(integration.category)} {integration.category}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    <p className="text-sm text-gray-600">
                      {integration.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Add Integration Modal */}
        {showAddModal && (
          <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Adicionar Nova Integração</DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {!selectedIntegration ? (
                  <>
                    <Label>Selecione uma integração:</Label>
                    <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                      {integrationTypes
                        .filter(type => !integrations.some(i => i.type === type.id))
                        .map((integration) => (
                        <Card 
                          key={integration.id}
                          className="cursor-pointer hover:bg-gray-50 p-3"
                          onClick={() => setSelectedIntegration(integration.id)}
                        >
                          <div className="flex items-center space-x-2">
                            <span className="text-2xl">{integration.icon}</span>
                            <div>
                              <div className="font-medium text-sm">{integration.name}</div>
                              <div className="text-xs text-gray-500">{integration.category}</div>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Configuration Form */}
                    {(() => {
                      const integration = integrationTypes.find(i => i.id === selectedIntegration);
                      
                      return (
                        <div className="space-y-4">
                          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                            <span className="text-3xl">{integration.icon}</span>
                            <div>
                              <h3 className="font-medium">{integration.name}</h3>
                              <p className="text-sm text-gray-600">{integration.description}</p>
                            </div>
                          </div>
                          
                          {integration.fields.map((field) => (
                            <div key={field.key} className="space-y-2">
                              <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
                              
                              {field.type === 'boolean' ? (
                                <div className="flex items-center space-x-2">
                                  <Switch
                                    checked={config[field.key] || false}
                                    onCheckedChange={(checked) => 
                                      setConfig(prev => ({ ...prev, [field.key]: checked }))
                                    }
                                  />
                                  <span className="text-sm">{field.label}</span>
                                </div>
                              ) : field.type === 'select' ? (
                                <Select
                                  value={config[field.key] || ''}
                                  onValueChange={(value) => 
                                    setConfig(prev => ({ ...prev, [field.key]: value }))
                                  }
                                >
                                  <SelectTrigger>
                                    <SelectValue placeholder={`Selecione ${field.label}`} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {field.options.map((option) => (
                                      <SelectItem key={option} value={option}>
                                        {option}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input
                                  type={field.type}
                                  placeholder={field.label}
                                  value={config[field.key] || ''}
                                  onChange={(e) => 
                                    setConfig(prev => ({ ...prev, [field.key]: e.target.value }))
                                  }
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </>
                )}

                <div className="flex justify-end space-x-2 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setShowAddModal(false);
                      setSelectedIntegration(null);
                      setConfig({});
                    }}
                  >
                    Cancelar
                  </Button>
                  {selectedIntegration && (
                    <Button onClick={handleAddIntegration} disabled={loading}>
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Adicionando...
                        </>
                      ) : (
                        <>
                          <span className="mr-2">✅</span>
                          Adicionar Integração
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}
